import useNow from '@/hooks/useNow';
import {
  reportingClock, type CarePeriodInput, type ClockEntry, type ClockState, type ClockTask,
} from '@/logic/carePeriodClock';
import {
  formatReportDate, CMS_OPERATION, inFlight, type CmsSubmissionLike,
} from '@/logic/cmsWindows';

// The six CMS milestones, each carrying its own submit button. This block replaced the Status
// card: the care-period badge and dates live in the header, the MBI behind "See more", and every
// window reason now sits on the milestone it belongs to instead of a bullet list underneath.

type Tone = 'good' | 'moderate' | 'bad' | 'muted';

const TONE: Record<ClockState, Tone> = {
  submitted: 'good',
  open: 'moderate',
  overdue: 'bad',
  upcoming: 'muted',
  blocked: 'muted',
  na: 'muted',
};

const EDGE: Record<Tone, string> = {
  good: 'var(--wi-status-good)',
  moderate: 'var(--wi-status-moderate)',
  bad: 'var(--wi-status-bad)',
  muted: 'var(--wi-divider)',
};

function ClockIcon({ state }: { state: ClockState }) {
  if (state === 'submitted') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--wi-text-primary-good)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  if (state === 'open' || state === 'overdue') {
    const stroke = state === 'overdue' ? 'var(--wi-text-primary-bad)' : 'var(--wi-text-primary-moderate)';
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--wi-text-placeholder)" strokeWidth="2.5" aria-hidden="true">
      <circle cx="12" cy="12" r="9" strokeDasharray="4 4" />
    </svg>
  );
}

interface ReportingClockProps {
  carePeriod?: CarePeriodInput | null;
  cmsSubmissions?: readonly CmsSubmissionLike[] | null;
  tasks?: readonly ClockTask[] | null;
  now?: number;
  /** Fires the CMS reporting submission for a milestone; absent for a read-only viewer. */
  onSubmit?: ((kind: 'baseline' | 'quarterly' | 'final') => void) | null;
  /** Care-period lifecycle actions, shown on the row under the chips. */
  onAlign?: (() => void) | null;
  onRunEligibility?: (() => void) | null;
}

export default function ReportingClock({
  carePeriod, cmsSubmissions = null, tasks = [], now, onSubmit = null, onAlign = null, onRunEligibility = null,
}: ReportingClockProps) {
  const ticking = useNow();
  const at = now ?? ticking;
  if (!carePeriod) {
    return (
      <section className="cms-clock" aria-label="CMS reporting clock">
        <div className="cms-clock__head"><span className="eyebrow text-secondary">CMS reporting clock</span></div>
        <p className="body2 text-secondary" style={{ margin: 0 }}>No care period yet.</p>
      </section>
    );
  }
  const entries = reportingClock(carePeriod, cmsSubmissions, tasks, at);
  const status = String(carePeriod.status ?? '');

  const lifecycle = (() => {
    if ((status === 'pending' || status === 'pending_eligibility') && onRunEligibility) {
      const busy = inFlight(cmsSubmissions, CMS_OPERATION.eligibility);
      return { label: 'Check eligibility', onClick: onRunEligibility, busy };
    }
    if (status === 'pending_alignment' && onAlign) {
      const busy = inFlight(cmsSubmissions, CMS_OPERATION.alignment);
      return { label: 'Align', onClick: onAlign, busy };
    }
    return null;
  })();

  return (
    <section className="cms-clock" aria-label="CMS reporting clock">
      <div className="cms-clock__head">
        <span className="eyebrow text-secondary">CMS reporting clock</span>
        <span className="detail1" style={{ color: 'var(--wi-text-placeholder)' }}>
          quarterlies open 70–110 days after the prior accepted report
        </span>
      </div>
      <div className="cms-clock__track">
        {entries.map((e: ClockEntry) => {
          const { action } = e;
          return (
            <div key={e.slot} className="cms-chip" style={{ borderTopColor: EDGE[TONE[e.state]] }}>
              <div className="cms-chip__label">
                <ClockIcon state={e.state} />
                {e.label}
              </div>
              <div className="detail1 text-secondary cms-chip__when">
                {e.submittedAt != null ? formatReportDate(e.submittedAt) : e.hint}
              </div>
              {e.submittedAt != null && <div className="cms-chip__sub">{e.hint}</div>}
              {action && onSubmit && (
              <>
                {action.reason && (
                  <p className="cms-chip__why" id={`cms-why-${e.slot}`}>{action.reason}</p>
                )}
                <button
                  type="button"
                  className="cms-chip__btn"
                  disabled={!action.enabled}
                  aria-label={`Submit ${e.label} report to CMS`}
                  aria-describedby={action.reason ? `cms-why-${e.slot}` : undefined}
                  onClick={() => onSubmit(action.kind)}
                >
                  Submit
                </button>
              </>
              )}
            </div>
          );
        })}
      </div>
      {lifecycle && (
        <div className="cms-clock__actions">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={lifecycle.busy}
            title={lifecycle.busy ? 'A submission is already in flight' : undefined}
            onClick={lifecycle.onClick}
          >
            {lifecycle.label}
          </button>
        </div>
      )}
      {status === 'pending_unalignment' && (
        <p className="detail1 text-secondary cms-clock__note">Unalignment in progress.</p>
      )}
    </section>
  );
}
